import { DigitalCatalog, DigitalDetail } from './DigitalMarket';
import { ProjectsIntroduction } from './ProjectsIntroduction';
import { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import {
  Avatar,
  Brand,
  ButtonLink,
  IconButton,
  AuthPage,
  Empty,
  LoginRequired,
} from '@yingbaba/ui';
import { Menu, X, ArrowRight } from '@yingbaba/icons';
import { useSession } from '@yingbaba/api';
import { siteURL } from '@yingbaba/utils';
import { Home } from './Home';
import { Catalog, Detail } from './Market';
import { AboutPage, HelpPage, FeaturePage } from './Information';
import { Publish } from './Publish';
import { titleForPath, descriptionForPath } from './metadata';
import { LegalPage } from './Legal';
import { SupportWidget } from './SupportWidget';
import { WechatConfirm } from './WechatConfirm';
const links = [
  ['/', '首页'],
  ['/digital-resources', '数字资源'],
  ['/projects', '项目协作'],
  ['/demands', '需求大厅'],
  ['/providers', '找服务商'],
  ['/resources', '影视资源'],
  ['/cases', '精选案例'],
];
export function App() {
  const [menu, setMenu] = useState(false);
  const session = useSession();

  const loc = useLocation();
  useEffect(() => {
    setMenu(false);
    if (!loc.hash) window.scrollTo(0, 0);
    if (!/^\/(providers|demands|resources|cases|digital-resources)\/[^/]+$/.test(loc.pathname)) {
      document.title = titleForPath(loc.pathname);
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute('content', descriptionForPath(loc.pathname));
      document
        .querySelector('meta[property="og:title"]')
        ?.setAttribute('content', titleForPath(loc.pathname));
      document
        .querySelector('meta[property="og:description"]')
        ?.setAttribute('content', descriptionForPath(loc.pathname));
    }
  }, [loc.pathname]);
  useEffect(() => {
    if (!loc.hash) return;
    let id: string;
    try {
      id = decodeURIComponent(loc.hash.slice(1));
    } catch {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (target instanceof HTMLDetailsElement) target.open = true;
      target?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [loc.pathname, loc.hash]);
  useEffect(() => {
    if (!menu) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenu(false);
        document.getElementById('site-menu-toggle')?.focus();
      }
    };
    const closeOnDesktop = () => {
      if (window.innerWidth > 980) setMenu(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnDesktop);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnDesktop);
    };
  }, [menu]);
  return (
    <>
      <a href="#main" className="skip-link">
        跳到主要内容
      </a>
      <header className="site-header">
        <div className="container header-inner">
          <Link to="/" aria-label="影爸爸首页">
            <Brand />
          </Link>
          <nav className="nav-links" aria-label="主导航">
            {links.map(([p, n]) => (
              <NavLink key={p} to={p} end={p === '/'}>
                {n}
              </NavLink>
            ))}
          </nav>
          <div className="header-actions">
            {session.data ? (
              <a className="header-login link" href={siteURL('work')}>
                <Avatar
                  name={session.data.account.name}
                  src={session.data.account.avatar_url}
                  size={26}
                />
                进入工作台
                <ArrowRight size={14} />
              </a>
            ) : (
              <Link className="header-login" to="/login">
                登录 / 注册
              </Link>
            )}
            <ButtonLink to="/publish" variant="primary">
              免费发布需求
            </ButtonLink>
            <IconButton
              id="site-menu-toggle"
              label={menu ? '关闭导航' : '打开导航'}
              className="mobile-toggle"
              aria-expanded={menu}
              aria-controls="mobile-site-nav"
              onClick={() => setMenu(!menu)}
            >
              {menu ? <X size={20} /> : <Menu size={20} />}
            </IconButton>
          </div>
        </div>
        {menu && (
          <>
            <button
              className="mobile-menu-overlay"
              aria-label="关闭移动导航遮罩"
              onClick={() => setMenu(false)}
            />
            <nav
              id="mobile-site-nav"
              className="mobile-menu"
              aria-label="移动导航"
              onClick={(event) => {
                if (event.target instanceof Element && event.target.closest('a[href]'))
                  setMenu(false);
              }}
            >
              <div className="mobile-menu-primary">
                {links.map(([p, n]) => (
                  <NavLink key={p} to={p} end={p === '/'}>
                    {n}
                  </NavLink>
                ))}
                <NavLink to="/ai">AI 创作</NavLink>
                <NavLink to="/promotion">视频推广</NavLink>
              </div>
              <div className="mobile-menu-actions">
                <ButtonLink to="/publish" variant="primary" className="btn-wide">
                  免费发布需求
                </ButtonLink>
                {session.data ? (
                  <a className="btn btn-secondary btn-wide" href={siteURL('work')}>
                    进入工作台
                    <ArrowRight size={14} />
                  </a>
                ) : (
                  <Link className="btn btn-secondary btn-wide" to="/login">
                    登录 / 注册
                  </Link>
                )}
              </div>
            </nav>
          </>
        )}
      </header>
      <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          {['demands', 'providers', 'resources', 'cases'].map((kind) => (
            <Route key={kind} path={`/${kind}`} element={<Catalog kind={kind} />} />
          ))}
          {['demands', 'providers', 'resources', 'cases'].map((kind) => (
            <Route key={kind} path={`/${kind}/:id`} element={<Detail kind={kind} />} />
          ))}
          <Route
            path="/publish"
            element={
              <LoginRequired>
                <Publish key={loc.search} />
              </LoginRequired>
            }
          />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/wechat/confirm" element={<WechatConfirm />} />
          <Route path="/digital-resources" element={<DigitalCatalog />} />
          <Route path="/digital-resources/:id" element={<DigitalDetail />} />
          <Route path="/projects" element={<ProjectsIntroduction />} />
          <Route path="/ai" element={<FeaturePage kind="ai" />} />
          <Route path="/promotion" element={<FeaturePage kind="promotion" />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route
            path="*"
            element={
              <Empty
                title="页面未找到"
                description="链接可能已变更或过期。你可以返回首页，或到帮助中心查找需要的信息。"
                action={
                  <div className="information-actions">
                    <ButtonLink to="/">回到首页</ButtonLink>
                    <ButtonLink to="/help" variant="secondary">
                      帮助中心
                    </ButtonLink>
                  </div>
                }
              />
            }
          />
        </Routes>
      </main>
      {loc.pathname !== '/login' && <SupportWidget />}
      {loc.pathname !== '/login' && (
        <footer className="site-footer">
          <div className="container">
            <div className="footer-grid">
              <div>
                <Brand />
                <p>
                  连接每一个好创意与专业创作者。
                  <br />
                  从需求到成片，让合作更简单。
                </p>
              </div>
              <div className="footer-col">
                <strong>发现影爸爸</strong>
                <Link to="/digital-resources">寻找数字素材</Link>
                <Link to="/providers">寻找创作团队</Link>
                <Link to="/resources">探索影视资源</Link>
                <Link to="/cases">发现创作灵感</Link>
              </div>
              <div className="footer-col">
                <strong>开始创作</strong>
                <Link to="/projects">管理创作项目</Link>
                <Link to="/publish">发布拍片需求</Link>
                <Link to="/login?mode=register&role=provider&returnTo=%2Fprovider">成为服务商</Link>
                <Link to="/login?mode=register&role=resource&returnTo=%2Fresources">
                  成为资源方
                </Link>
                <Link to="/ai">AI 创作工作台</Link>
              </div>
              <div className="footer-col">
                <strong>了解更多</strong>
                <Link to="/about">关于影爸爸</Link>
                <Link to="/help">帮助中心</Link>
                <Link to="/promotion">视频推广</Link>
              </div>
            </div>
            <div className="footer-bottom">
              <span>© {new Date().getFullYear()} 影爸爸 YINGBABA · 为好作品而连接</span>
              <span>
                <Link to="/terms">服务条款</Link>　·　<Link to="/privacy">隐私政策</Link>
              </span>
            </div>
          </div>
        </footer>
      )}
    </>
  );
}
